import { Box, Checkbox, Divider, MenuItem, MenuList, SxProps, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useFormContext } from 'react-hook-form';
import { Product_Sub_Family_Name_Enum } from '@kheops/graphql';
import SearchInput from '../../../common/components/SearchInput';
import { companyPackagingsAtom } from '../../state';
import { insensitiveStringIncludes } from '../../../common/utils/common.utils';
import ProductSubFamilyFilter from '../../../products/common/ProductSubFamilyFilter';
import useSearchProducts from '../../../products/common/useSearchProducts';
import SelectFilterChip from '../../../common/components/SelectFilterChip';
import { DraftSubCatalog, displayedCompanyPackagingsAtom, filteredCompanyPackagingsAtom } from './state';

enum SelectedPackagingsCategories {
  ALL_PACKAGINGS,
  SELECTED_PACKAGINGS,
  NOT_SELECTED_PACKAGINGS,
}

interface SubCatalogPackagingsFormFiltersProps {
  sx?: SxProps;
}

export default function SubCatalogPackagingsFormFilters({ sx }: SubCatalogPackagingsFormFiltersProps): React.JSX.Element {
  const theme = useTheme();
  const isDownLg = useMediaQuery(theme.breakpoints.down('lg'));
  const { watch, setValue } = useFormContext<DraftSubCatalog>();
  const [selectedPackagingsCategory, setSelectedPackagingsCategory] = useState(SelectedPackagingsCategories.ALL_PACKAGINGS);
  const packagingsAvailability = watch('packagingsAvailability');
  const catalogPackagings = useAtomValue(companyPackagingsAtom);
  const [filteredCatalogPackagings, setFilteredCatalogPackagings] = useAtom(filteredCompanyPackagingsAtom);
  const setDisplayedCatalogPackagings = useSetAtom(displayedCompanyPackagingsAtom);
  const { t } = useTranslation(['common', 'catalogs']);

  const productFamiliesOptions = useMemo((): Product_Sub_Family_Name_Enum[] => {
    const options = new Set<Product_Sub_Family_Name_Enum>();

    catalogPackagings.forEach(({ product }) => options.add(product.sub_family as Product_Sub_Family_Name_Enum));

    return [...options.values()];
  }, [catalogPackagings]);

  const handleProductSearchChange = useCallback((productFamilies: Product_Sub_Family_Name_Enum[], query: string): void => {
    const newFilteredProducts = catalogPackagings.filter((packaging) => {
      const matchFamilies = !productFamilies.length || productFamilies.includes(packaging.product.sub_family as Product_Sub_Family_Name_Enum);
      const matchSearch = !query || insensitiveStringIncludes(packaging.product.name, query) || packaging.base_unit.gtin?.includes(query);

      return matchFamilies && matchSearch;
    });

    setFilteredCatalogPackagings(newFilteredProducts);
  }, [catalogPackagings]);

  const availableFiltredPackagings = filteredCatalogPackagings.filter((packaging) => packaging.sku in packagingsAvailability && packagingsAvailability[packaging.sku].isAvailable);
  const availableFilteredPackagingsSkusSet = new Set(availableFiltredPackagings.map(({ sku }) => sku));

  const handleGlobalCheckBox = (checked: boolean): void => {
    filteredCatalogPackagings.forEach((packaging) => {
      setValue(`packagingsAvailability.${packaging.sku}.isAvailable`, checked, { shouldDirty: true });
    });
  };

  const handleDisplayedPackagingsCategoryChange = (category: SelectedPackagingsCategories | null): void => {
    if (category !== null) {
      setSelectedPackagingsCategory(category);
    }
  };

  const {
    query,
    setQuery,
    setProductFamilies,
    selectedProductFamilyMap,
    productFamilyMap,

  } = useSearchProducts({ productFamiliesOptions, onChange: handleProductSearchChange });

  useEffect(() => {
    switch (selectedPackagingsCategory) {
      case SelectedPackagingsCategories.ALL_PACKAGINGS:
        setDisplayedCatalogPackagings(filteredCatalogPackagings);
        break;
      case SelectedPackagingsCategories.SELECTED_PACKAGINGS:
        setDisplayedCatalogPackagings(availableFiltredPackagings);
        break;
      case SelectedPackagingsCategories.NOT_SELECTED_PACKAGINGS:
        setDisplayedCatalogPackagings(filteredCatalogPackagings.filter(({ sku }) => !availableFilteredPackagingsSkusSet.has(sku)));
        break;
      default:
        setDisplayedCatalogPackagings([]);
    }
  }, [selectedPackagingsCategory, filteredCatalogPackagings]);

  const mobileCategoryMenu = (
    <MenuList>
      <MenuItem
        selected={selectedPackagingsCategory === SelectedPackagingsCategories.ALL_PACKAGINGS}
        onClick={() => handleDisplayedPackagingsCategoryChange(SelectedPackagingsCategories.ALL_PACKAGINGS)}
      >
        {t('catalogs:all_N', { count: filteredCatalogPackagings.length })}
      </MenuItem>
      <MenuItem
        selected={selectedPackagingsCategory === SelectedPackagingsCategories.SELECTED_PACKAGINGS}
        onClick={() => handleDisplayedPackagingsCategoryChange(SelectedPackagingsCategories.SELECTED_PACKAGINGS)}
      >
        {t('catalogs:selected_N', { count: availableFiltredPackagings.length })}
      </MenuItem>
      <MenuItem
        selected={selectedPackagingsCategory === SelectedPackagingsCategories.NOT_SELECTED_PACKAGINGS}
        onClick={() => handleDisplayedPackagingsCategoryChange(SelectedPackagingsCategories.NOT_SELECTED_PACKAGINGS)}
      >
        {t('catalogs:not_selected_N', { count: filteredCatalogPackagings.length - availableFiltredPackagings.length })}
      </MenuItem>
    </MenuList>
  );

  const displayedCategoryChangeComponent = useMemo(() => {
    if (isDownLg) {
      let currentLabel: string;

      if (selectedPackagingsCategory === SelectedPackagingsCategories.ALL_PACKAGINGS) {
        currentLabel = t('catalogs:all_N', { count: filteredCatalogPackagings.length });
      } else if (selectedPackagingsCategory === SelectedPackagingsCategories.SELECTED_PACKAGINGS) {
        currentLabel = t('catalogs:selected_N', { count: availableFiltredPackagings.length });
      } else {
        currentLabel = t('catalogs:not_selected_N', {
          count: filteredCatalogPackagings.length - availableFiltredPackagings.length,
        });
      }

      return (
        <SelectFilterChip
          label={currentLabel}
          isFilterActive={selectedPackagingsCategory !== SelectedPackagingsCategories.ALL_PACKAGINGS}
          content={mobileCategoryMenu}
        />
      );
    }

    return (

      <ToggleButtonGroup
        onChange={(_, category) => handleDisplayedPackagingsCategoryChange(category)}
        value={selectedPackagingsCategory}
        exclusive
        sx={{
          flex: 1,
        }}
      >
        <ToggleButton value={SelectedPackagingsCategories.ALL_PACKAGINGS}>
          <Typography variant="labelLarge">
            {t('catalogs:all_N', { count: filteredCatalogPackagings.length })}
          </Typography>
        </ToggleButton>
        <ToggleButton value={SelectedPackagingsCategories.SELECTED_PACKAGINGS}>
          <Typography variant="labelLarge">
            {t('catalogs:selected_N', { count: availableFiltredPackagings.length })}
          </Typography>
        </ToggleButton>
        <ToggleButton value={SelectedPackagingsCategories.NOT_SELECTED_PACKAGINGS}>
          <Typography variant="labelLarge">
            {t('catalogs:not_selected_N', { count: filteredCatalogPackagings.length - availableFiltredPackagings.length })}
          </Typography>
        </ToggleButton>
      </ToggleButtonGroup>
    );
  }, [isDownLg, selectedPackagingsCategory, filteredCatalogPackagings, availableFiltredPackagings]);

  return (
    <Box
      sx={{
        ...sx,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        width: '100%',
      }}
    >
      <Typography
        variant="titleMedium"
        sx={{
          display: {
            [theme.breakpoints.down('lg')]: {
              display: 'none',
            },
          },
        }}
      >
        {t('common:packaging_other')}
      </Typography>
      <SearchInput
        value={query}
        onChange={setQuery}
        sx={{
          width: {
            xs: '100%',
            lg: 400,
          },
          mt: 0.5,
        }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          py: 0.5,
          flexWrap: 'wrap',
        }}
      >
        <Checkbox
          indeterminate={availableFiltredPackagings.length < filteredCatalogPackagings.length && availableFiltredPackagings.length !== 0}
          onChange={(_, checked) => handleGlobalCheckBox(checked)}
          checked={availableFiltredPackagings.length === filteredCatalogPackagings.length}
        />
        {!isDownLg && (
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              my: 1,
            }}
          />
        )}
        {displayedCategoryChangeComponent}
        {!isDownLg && (
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              my: 1,
            }}
          />
        )}
        <ProductSubFamilyFilter
          productFamilyMap={productFamilyMap}
          selectedProductFamilyMap={selectedProductFamilyMap}
          onChange={(values) => setProductFamilies(values.map(({ value }) => value))}
        />
      </Box>
    </Box>
  );
}
